import { useState } from "react";
import { useAuditLog, useUsers, type AuditEntry } from "../api/hooks";
import { useAuth } from "../app/AuthProvider";
import { DataTable, Pagination, createColumnHelper } from "../components/DataTable";
import { ErrorNotice, Field, Input, PageHeader, Select } from "../components/ui";
import { auditActionLabel, formatDateTime, shortId } from "../lib/format";

/**
 * Vista de auditoría (CLAUDE.md Fase 4).
 *
 * El registro es de solo inserción: no hay forma de editarlo ni borrarlo, ni
 * desde aquí ni desde la API (lo refuerza un disparador en la base de datos).
 * Por eso la pantalla es únicamente de consulta.
 */

const column = createColumnHelper<AuditEntry>();

const COMMON_ACTIONS = [
  "deca.created",
  "deca.version_created",
  "deca.replaced",
  "deca.incident_updated",
  "deca.signed",
  "deca.download_disabled",
  "counterparty.created",
  "counterparty.updated",
  "vehicle.created",
  "vehicle.updated",
  "api_client.created",
  "api_client.deactivated",
  "template.updated",
  "template.logo_uploaded",
];

export function AuditPage() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [actorUserId, setActorUserId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { activeTenantId } = useAuth();
  const users = useUsers(activeTenantId);
  const filters = {
    page,
    pageSize: 25,
    ...(action ? { action } : {}),
    ...(actorUserId ? { actorUserId } : {}),
    ...(dateFrom ? { dateFrom: new Date(`${dateFrom}T00:00:00`).toISOString() } : {}),
    ...(dateTo ? { dateTo: new Date(`${dateTo}T23:59:59`).toISOString() } : {}),
  };
  const query = useAuditLog(filters);

  const columns = [
    column.accessor("createdAt", {
      header: "Cuándo",
      cell: (info) => <span className="num whitespace-nowrap">{formatDateTime(info.getValue())}</span>,
    }),
    column.accessor("action", {
      header: "Qué se hizo",
      cell: (info) => auditActionLabel(info.getValue()),
    }),
    column.display({
      id: "actor",
      header: "Quién",
      cell: (info) => {
        const entry = info.row.original;
        if (entry.actorType === "SYSTEM") return "Sistema";
        if (entry.actorType === "API_CLIENT") {
          return <span className="ident text-xs">API {shortId(entry.actorApiClientId)}</span>;
        }
        const user = users.data?.items.find((item) => item.id === entry.actorUserId);
        return (
          <span>
            {user?.fullName ?? <span className="ident text-xs">{shortId(entry.actorUserId)}</span>}
            {entry.actorIsSuperAdmin ? " (super-admin)" : ""}
          </span>
        );
      },
    }),
    column.display({
      id: "document",
      header: "Documento",
      cell: (info) =>
        info.row.original.decaDocumentId ? (
          <span className="ident text-xs">{shortId(info.row.original.decaDocumentId)}</span>
        ) : (
          "—"
        ),
    }),
    column.accessor("changesJson", {
      header: "Detalle",
      cell: (info) => {
        const value = info.getValue();
        if (!value) return "—";
        const text = JSON.stringify(value);
        return (
          <span className="ident text-xs" title={text}>
            {text.length > 90 ? `${text.slice(0, 90)}…` : text}
          </span>
        );
      },
    }),
    column.display({
      id: "cross",
      header: "Suplantación",
      cell: (info) =>
        info.row.original.actedAsTenantId ? (
          <span className="ident text-xs">como {shortId(info.row.original.actedAsTenantId)}</span>
        ) : (
          "—"
        ),
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Control"
        title="Auditoría"
        description="Registro de todo lo que ocurre con los documentos y la configuración de la empresa. Es de solo inserción: nadie puede modificarlo ni borrarlo, ni siquiera un administrador."
      />

      <div className="surface mb-4 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Operación" htmlFor="au-action">
          <Select
            id="au-action"
            value={action}
            onChange={(event) => {
              setAction(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Todas</option>
            {COMMON_ACTIONS.map((item) => (
              <option key={item} value={item}>
                {auditActionLabel(item)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Persona" htmlFor="au-user">
          <Select
            id="au-user"
            value={actorUserId}
            onChange={(event) => {
              setActorUserId(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Cualquiera</option>
            {(users.data?.items ?? []).map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Desde" htmlFor="au-from">
          <Input
            id="au-from"
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setPage(1);
            }}
          />
        </Field>
        <Field label="Hasta" htmlFor="au-to">
          <Input
            id="au-to"
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setPage(1);
            }}
          />
        </Field>
      </div>

      {query.isError ? (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={query.data?.items ?? []}
            isLoading={query.isLoading}
            emptyMessage="No hay movimientos con estos filtros."
          />
          <Pagination
            page={query.data?.page ?? page}
            pageSize={query.data?.pageSize ?? 25}
            total={query.data?.total ?? 0}
            onPageChange={setPage}
          />
        </>
      )}
    </>
  );
}
